import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar, Users, Eye, ListChecks, FloppyDisk, UserCirclePlus, Copy, Plus, Trash, QrCode, DownloadSimple, ShieldCheck, Broadcast, ArrowSquareOut, CurrencyDollar, IdentificationCard } from "@phosphor-icons/react";
import GuideLink from "@/components/GuideLink";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 p-6">
      <Icon size={22} weight="duotone" className="text-[#1F6FE5] mb-3" />
      <div className="text-3xl font-display font-bold tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{label}</div>
    </div>
  );
}

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const toLocal = (s) => { if (!s) return ""; const d = new Date(s); const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); };

function NominationRound({ el, onChange }) {
  const [elections, setElections] = useState([]);
  const load = () => api.get("/elections").then(({ data }) => setElections(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const others = elections.filter((e) => e.id !== el.id && e.type !== undefined);
  const linkedRound = elections.find((e) => e.nomination_for === el.id);
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const [deadline, setDeadline] = useState(toLocalInput(el.nomination_deadline));
  useEffect(() => { setDeadline(toLocalInput(el.nomination_deadline)); }, [el.nomination_deadline]);
  const [excluded, setExcluded] = useState((el.nomination_excluded || []).join("\n"));
  useEffect(() => { setExcluded((el.nomination_excluded || []).join("\n")); }, [el.nomination_excluded]);
  const saveExcluded = async () => {
    const list = excluded.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put(`/elections/${el.id}`, { nomination_excluded: list });
      toast.success(list.length ? `${list.length} excluded ${list.length === 1 ? "person" : "people"} saved` : "Exclusion list cleared");
      onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveDeadline = async () => {
    try {
      const { data } = await api.put(`/elections/${el.id}`, { nomination_deadline: deadline ? new Date(deadline).toISOString() : "" });
      if (data.warning) toast.warning(data.warning);
      else toast.success(deadline ? "Nomination deadline saved" : "Deadline cleared");
      onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setTarget = async (targetId) => {
    try { await api.put(`/elections/${el.id}`, { nomination_for: targetId }); toast.success(targetId ? "Linked as nomination round" : "Unlinked"); onChange(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const promote = async (force = false) => {
    try {
      const { data } = await api.post(`/elections/${el.id}/promote-nominees`, null, { params: { force } });
      toast.success(`Promoted ${data.promoted}, updated ${data.updated}, skipped ${data.skipped.length}`);
      onChange();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.includes("still open")) {
        if (window.confirm("The nomination round is still open. Promote anyway?")) return promote(true);
      } else toast.error(apiError(detail));
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="nomination-round-card">
      <div className="font-medium text-sm flex items-center gap-1.5 mb-1"><ListChecks size={16} weight="duotone" className="text-[#1F6FE5]" /> Nomination round <GuideLink section="nominations" className="ml-auto" /></div>
      <p className="text-xs text-zinc-500 mb-3">Use a self-registration action as a nomination form, then promote approved nominees into a target election's candidates.</p>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">This action nominates candidates for</Label>
      <select data-testid="nomination-target-select" value={el.nomination_for || ""} onChange={(e) => setTarget(e.target.value)}
        className="mt-1.5 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white">
        <option value="">— Not a nomination round —</option>
        {others.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
      </select>
      {linkedRound && (
        <div className="mt-4 border-t border-zinc-100 pt-4">
          <p className="text-sm text-zinc-600 mb-2">Nomination round <strong>{linkedRound.title}</strong> is linked to this election.</p>
          <button data-testid="promote-nominees-btn" onClick={() => promote(false)} disabled={el.status !== "draft"}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F6FE5] text-white rounded-md font-semibold text-sm hover:bg-[#1552C9] transition-colors active:scale-95 disabled:opacity-50">
            <UserCirclePlus size={15} weight="fill" /> Promote approved nominees
          </button>
          {el.status !== "draft" && <p className="text-xs text-zinc-400 mt-1.5">Promotion is only available while this election is a draft.</p>}
        </div>
      )}
      {el.nomination_for && (
        <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="nomination-deadline-card">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Nomination deadline</Label>
          <p className="text-xs text-zinc-400 mt-0.5 mb-2">After this time the nomination link closes automatically. Leave empty for no cut-off.</p>
          <div className="flex gap-2">
            <input type="datetime-local" data-testid="nomination-deadline-input" value={deadline} onChange={(e) => setDeadline(e.target.value)}
              className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white" />
            <button data-testid="nomination-deadline-save" onClick={saveDeadline}
              className="px-4 py-2 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">Save</button>
          </div>
          {el.nomination_deadline && <p className="text-xs text-zinc-500 mt-1.5" data-testid="nomination-deadline-current">Closes {new Date(el.nomination_deadline).toLocaleString()}</p>}
        </div>
      )}
      {el.nomination_for && (
        <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="nomination-excluded-card">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Excluded people</Label>
          <p className="text-xs text-zinc-400 mt-0.5 mb-2">One name or email per line. Anyone listed can't submit a nomination and can't be nominated by others.</p>
          <textarea data-testid="nomination-excluded-input" value={excluded} onChange={(e) => setExcluded(e.target.value)} rows={3}
            placeholder={"jane@example.com\nJohn Mensah"}
            className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white font-mono" />
          <div className="flex items-center gap-3 mt-2">
            <button data-testid="nomination-excluded-save" onClick={saveExcluded}
              className="px-4 py-2 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">Save</button>
            {(el.nomination_excluded || []).length > 0 && <span className="text-xs text-zinc-500" data-testid="nomination-excluded-count">{el.nomination_excluded.length} excluded</span>}
          </div>
        </div>
      )}
      {el.nomination_for && <NominationFee el={el} onChange={onChange} />}
    </div>
  );
}

function NominationFee({ el, onChange }) {
  const init = el.nomination_fee || {};
  const [enabled, setEnabled] = useState(!!init.enabled);
  const [mode, setMode] = useState(init.mode || "uniform");
  const [amount, setAmount] = useState(init.amount || "");
  const [hours, setHours] = useState(Math.round((init.expiry_minutes || 1440) / 60));
  const [perPos, setPerPos] = useState(init.per_position || {});
  const [positions, setPositions] = useState([]);
  const [saving, setSaving] = useState(false);
  const cur = init.currency || "GH₵";

  useEffect(() => {
    api.get(`/elections/${el.nomination_for}`).then(({ data }) => setPositions(data.positions || [])).catch(() => {});
  }, [el.nomination_for]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/elections/${el.id}/nomination-fee`, {
        enabled,
        mode,
        amount: parseFloat(amount) || 0,
        per_position: mode === "per_position"
          ? Object.fromEntries(Object.entries(perPos).map(([k, v]) => [k, parseFloat(v) || 0]))
          : {},
        expiry_minutes: Math.max(5, (parseInt(hours, 10) || 24) * 60),
      });
      toast.success(enabled ? "Nomination fee saved" : "Nomination fee disabled");
      onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="nomination-fee-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-sm flex items-center gap-1.5"><CurrencyDollar size={16} weight="duotone" className="text-[#1F6FE5]" /> Nomination fee</div>
          <p className="text-xs text-zinc-500 mt-0.5">Charge nominees a fee (Paystack) before their entry enters vetting. Pro plan.</p>
        </div>
        <Switch data-testid="nomfee-toggle" checked={enabled} onCheckedChange={setEnabled} disabled={el.status !== "draft"} />
      </div>
      {enabled && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            <button type="button" data-testid="nomfee-mode-uniform" onClick={() => setMode("uniform")} className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === "uniform" ? "bg-[#1F6FE5] text-white border-[#1F6FE5]" : "bg-white border-zinc-300 text-zinc-600"}`}>Uniform</button>
            <button type="button" data-testid="nomfee-mode-per" onClick={() => setMode("per_position")} className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${mode === "per_position" ? "bg-[#1F6FE5] text-white border-[#1F6FE5]" : "bg-white border-zinc-300 text-zinc-600"}`}>Per position</button>
          </div>
          {mode === "uniform" ? (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Fee amount ({cur})</Label>
              <Input data-testid="nomfee-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 max-w-[200px]" placeholder="50.00" />
            </div>
          ) : (
            <div className="space-y-2">
              {positions.map((p) => (
                <div key={p.id} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-600 flex-1 truncate">{p.title}</span>
                  <Input data-testid={`nomfee-pos-${p.id}`} type="number" min="0" step="0.01" value={perPos[p.title] ?? ""} onChange={(e) => setPerPos({ ...perPos, [p.title]: e.target.value })} className="max-w-[130px]" placeholder={`${cur} 0.00`} />
                </div>
              ))}
              {positions.length === 0 && <p className="text-xs text-zinc-400">Add positions to the target election first.</p>}
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Unpaid entries expire after (hours)</Label>
            <Input data-testid="nomfee-expiry" type="number" min="1" max="336" value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1.5 max-w-[130px]" />
          </div>
        </div>
      )}
      {el.status !== "draft" && <p className="text-xs text-amber-600 mt-2">Fees can only be changed while the round is a draft.</p>}
      <button data-testid="nomfee-save" onClick={save} disabled={saving || el.status !== "draft"} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95 disabled:opacity-50">
        <FloppyDisk size={15} weight="fill" /> Save fee
      </button>
    </div>
  );
}

export default function OverviewTab({ data, onChange }) {
  const el = data.election;
  const editable = el.status !== "closed";
  const [f, setF] = useState({ title: el.title, description: el.description || "", starts_at: toLocal(el.starts_at), ends_at: toLocal(el.ends_at), results_visibility: el.results_visibility });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const candidateCount = data.positions.reduce((a, p) => a + (p.candidates?.length || 0), 0);
  const [selfReg, setSelfReg] = useState(!!el.allow_self_registration);
  const [ussd, setUssd] = useState(!!el.ussd_enabled);
  const [liveMon, setLiveMon] = useState(el.live_monitor !== false);
  const liveLink = `${window.location.origin}/live/${el.id}`;
  const toggleLive = async (val) => {
    setLiveMon(val);
    try { await api.put(`/elections/${el.id}`, { live_monitor: val }); toast.success(val ? "Live monitoring enabled — anyone with the link can watch turnout & tallies" : "Live monitoring disabled"); onChange(); }
    catch (e) { setLiveMon(!val); toast.error(apiError(e.response?.data?.detail)); }
  };
  const copyLive = async () => {
    try { await navigator.clipboard.writeText(liveLink); toast.success("Live monitor link copied"); }
    catch { toast.error("Copy failed — link: " + liveLink); }
  };
  const toggleUssd = async (val) => {
    setUssd(val);
    try { await api.put(`/elections/${el.id}`, { ussd_enabled: val }); toast.success(val ? "USSD voting enabled — voters get a PIN by SMS" : "USSD voting disabled"); onChange(); }
    catch (e) { setUssd(!val); toast.error(apiError(e.response?.data?.detail)); }
  };
  const [approval, setApproval] = useState(!!el.require_registration_approval);
  const [fields, setFields] = useState(el.register_fields || []);
  const [savingFields, setSavingFields] = useState(false);
  const qrWrap = useRef(null);
  const regLink = `${window.location.origin}/register/${el.id}`;
  const genKey = () => `f_${Math.random().toString(36).slice(2, 8)}`;

  const toggleSelfReg = async (val) => {
    setSelfReg(val);
    try { await api.put(`/elections/${el.id}`, { allow_self_registration: val }); toast.success(val ? "Self-registration enabled" : "Self-registration disabled"); onChange(); }
    catch (e) { setSelfReg(!val); toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleApproval = async (val) => {
    setApproval(val);
    try { await api.put(`/elections/${el.id}`, { require_registration_approval: val }); toast.success(val ? "Approval required for new sign-ups" : "Sign-ups auto-approved"); onChange(); }
    catch (e) { setApproval(!val); toast.error(apiError(e.response?.data?.detail)); }
  };
  const addField = () => setFields([...fields, { key: genKey(), label: "", required: true, type: "text", options: [] }]);
  const patchField = (i, patch) => setFields(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const saveFields = async () => {
    const clean = fields
      .map((f) => ({ ...f, label: f.label.trim(), type: f.type || "text", options: f.type === "dropdown" ? (f.options || []).map((o) => o.trim()).filter(Boolean) : [] }))
      .filter((f) => f.label);
    setSavingFields(true);
    try { await api.put(`/elections/${el.id}`, { register_fields: clean }); setFields(clean); toast.success("Custom fields saved"); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingFields(false); }
  };
  const copyReg = async () => {
    try { await navigator.clipboard.writeText(regLink); toast.success("Registration link copied"); }
    catch { toast.error("Copy failed — link: " + regLink); }
  };
  const downloadQR = () => {
    const canvas = qrWrap.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `selectflow_register_${el.id}.png`;
    a.click();
    toast.success("QR code downloaded");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/elections/${el.id}`, {
        title: f.title, description: f.description,
        starts_at: new Date(f.starts_at).toISOString(), ends_at: new Date(f.ends_at).toISOString(),
        results_visibility: f.results_visibility,
      });
      toast.success("Saved");
      onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Turnout" value={`${el.turnout.pct}%`} />
        <Stat icon={ListChecks} label="Positions" value={data.positions.length} />
        <Stat icon={Users} label="Candidates" value={candidateCount} />
        <Stat icon={Eye} label="Results" value={el.results_visibility === "live" ? "Live" : "At close"} />
      </div>

      <div className="bg-white border border-zinc-200 p-6 max-w-2xl">
        <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><Calendar size={20} weight="duotone" /> Schedule</h3>
        {!editable ? (
          <div className="mt-4 grid grid-cols-2 gap-6 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Opens</div>{fmt(el.starts_at)}</div>
            <div><div className="text-xs uppercase tracking-widest text-zinc-400 mb-1">Closes</div>{fmt(el.ends_at)}</div>
            <div className="col-span-2 text-xs font-mono text-zinc-400">TIMEZONE · {el.timezone}</div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
              <Input data-testid="ov-title" value={f.title} onChange={set("title")} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
              <Textarea data-testid="ov-desc" value={f.description} onChange={set("description")} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Opens</Label>
                <Input data-testid="ov-start" type="datetime-local" value={f.starts_at} onChange={set("starts_at")} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Closes</Label>
                <Input data-testid="ov-end" type="datetime-local" value={f.ends_at} onChange={set("ends_at")} className="mt-1.5" />
              </div>
            </div>
            <button data-testid="ov-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">
              <FloppyDisk size={16} weight="fill" /> Save changes
            </button>
          </div>
        )}
      </div>

      <NominationRound el={el} onChange={onChange} />

      <div className="bg-white border border-zinc-200 p-6 max-w-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><Users size={20} weight="duotone" /> Feature-phone voting (USSD)</h3>
            <p className="text-sm text-zinc-500">For voters without a smartphone. Each voter gets a numeric PIN by SMS and votes by dialling your USSD code — no internet needed. Requires a phone number on each voter.</p>
          </div>
          <Switch data-testid="ussd-toggle" checked={ussd} onCheckedChange={toggleUssd} disabled={el.status === "closed"} />
        </div>
        {ussd && (
          <p className="mt-3 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
            When you publish, voters with a phone number receive a PIN by SMS plus your USSD dial code. Configure the Hubtel USSD service code and point its callback at <span className="font-mono">/api/ussd/hubtel</span>.
          </p>
        )}
      </div>

      <div className="bg-white border border-zinc-200 p-6 max-w-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><Broadcast size={20} weight="duotone" /> Live monitoring board</h3>
            <p className="text-sm text-zinc-500">A public, real-time page showing turnout and live tallies. Share it with voters and observers so everyone can watch the count transparently. Ballots stay anonymous — no counts are ever linked to a voter.</p>
          </div>
          <Switch data-testid="live-toggle" checked={liveMon} onCheckedChange={toggleLive} />
        </div>
        {liveMon && (
          <div className="mt-5 space-y-3">
            <div className="flex items-center gap-2">
              <input readOnly value={liveLink} data-testid="live-link" className="flex-1 text-sm font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-zinc-600" />
              <button data-testid="copy-live" onClick={copyLive} className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><Copy size={15} /> Copy</button>
              <a data-testid="open-live" href={liveLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><ArrowSquareOut size={15} /> Open</a>
            </div>
            {!el.published && <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">The live board becomes available once you publish this election.</p>}
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 p-6 max-w-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><UserCirclePlus size={20} weight="duotone" /> Voter self-registration</h3>
            <p className="text-sm text-zinc-500">Let voters add themselves to the roll via a public link. New sign-ups get their voting link automatically once the election is open.</p>
          </div>
          <Switch data-testid="selfreg-toggle" checked={selfReg} onCheckedChange={toggleSelfReg} disabled={el.status === "closed"} />
        </div>
        {selfReg && (
          <div className="mt-5 space-y-6">
            <div className="flex items-center gap-2">
              <input readOnly value={regLink} data-testid="selfreg-link" className="flex-1 text-sm font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-zinc-600" />
              <button data-testid="copy-selfreg" onClick={copyReg} className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><Copy size={15} /> Copy</button>
            </div>

            {/* QR code */}
            <div className="flex items-center gap-5 border border-zinc-200 rounded-lg p-4 bg-zinc-50/60">
              <div ref={qrWrap} data-testid="selfreg-qr" className="bg-white p-2.5 rounded-md border border-zinc-200 shrink-0">
                <QRCodeCanvas value={regLink} size={116} level="M" includeMargin={false} fgColor="#0A0A0A" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5"><QrCode size={16} weight="duotone" className="text-[#1F6FE5]" /> Scan-to-register QR</div>
                <p className="text-xs text-zinc-500 mt-1">Put this on a poster or slide so voters can join from their phone.</p>
                <button data-testid="download-qr-btn" onClick={downloadQR} className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 rounded-md text-xs font-medium hover:border-black transition-colors"><DownloadSimple size={14} /> Download PNG</button>
              </div>
            </div>

            {/* Approval */}
            <div className="flex items-start justify-between gap-4 border-t border-zinc-100 pt-5">
              <div>
                <div className="font-medium text-sm flex items-center gap-1.5"><ShieldCheck size={16} weight="duotone" className="text-[#1F6FE5]" /> Require approval</div>
                <p className="text-xs text-zinc-500 mt-1">Review each sign-up before they get a ballot link. Pending voters appear in the Voters tab.</p>
              </div>
              <Switch data-testid="approval-toggle" checked={approval} onCheckedChange={toggleApproval} disabled={el.status === "closed"} />
            </div>

            {/* Custom fields */}
            <div className="border-t border-zinc-100 pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">Custom registration fields</div>
                  <p className="text-xs text-zinc-500 mt-1">Ask registrants for extra info like a membership ID.</p>
                </div>
                <button data-testid="add-field-btn" onClick={addField} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 rounded-md text-xs font-medium hover:border-black transition-colors"><Plus size={14} /> Add field</button>
              </div>
              {fields.length > 0 && (
                <div className="mt-3 space-y-2">
                  {fields.map((f, i) => (
                    <div key={f.key} data-testid={`field-row-${i}`} className="border border-zinc-200 rounded-md p-3 space-y-2 bg-zinc-50/40">
                      <div className="flex items-center gap-2">
                        <Input data-testid={`field-label-${i}`} value={f.label} onChange={(e) => patchField(i, { label: e.target.value })} placeholder="Field label (e.g. Member ID)" className="flex-1 bg-white" />
                        <select data-testid={`field-type-${i}`} value={f.type || "text"} onChange={(e) => patchField(i, { type: e.target.value })} className="h-10 rounded-md border border-zinc-300 bg-white px-2 text-sm shrink-0">
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="dropdown">Dropdown</option>
                          <option value="checkbox">Checkbox</option>
                        </select>
                        <label className="flex items-center gap-1.5 text-xs text-zinc-500 shrink-0 select-none">
                          <input type="checkbox" data-testid={`field-required-${i}`} checked={f.required} onChange={(e) => patchField(i, { required: e.target.checked })} className="accent-[#1F6FE5]" /> Required
                        </label>
                        <button data-testid={`field-del-${i}`} onClick={() => removeField(i)} className="text-zinc-300 hover:text-red-500 transition-colors shrink-0"><Trash size={16} /></button>
                      </div>
                      {f.type === "dropdown" && (
                        <Input data-testid={`field-options-${i}`} value={(f.options || []).join(", ")} onChange={(e) => patchField(i, { options: e.target.value.split(",") })} placeholder="Options, comma-separated (e.g. Gold, Silver, Bronze)" className="bg-white text-sm" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button data-testid="save-fields-btn" onClick={saveFields} disabled={savingFields} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95 disabled:opacity-50">
                <FloppyDisk size={15} weight="fill" /> Save fields
              </button>
            </div>
          </div>
        )}
      </div>
      <KioskCard el={el} onChange={onChange} />
    </div>
  );
}

function KioskCard({ el, onChange }) {
  const [enabled, setEnabled] = useState(!!el.kiosk_enabled);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!el.kiosk_enabled) return;
    api.get(`/elections/${el.id}/kiosk/status`).then(({ data }) => setCount(data.total_with_codes)).catch(() => {});
  }, [el.id, el.kiosk_enabled]);

  const toggle = async (val) => {
    setEnabled(val);
    try { await api.put(`/elections/${el.id}`, { kiosk_enabled: val }); toast.success(val ? "Kiosk voting enabled" : "Kiosk voting disabled"); onChange(); }
    catch (e) { setEnabled(!val); toast.error(apiError(e.response?.data?.detail)); }
  };
  const generate = async (regenerate = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/elections/${el.id}/kiosk/generate`, null, { params: { regenerate } });
      setCount(data.total_with_codes);
      toast.success(`${data.generated} new code(s) · ${data.total_with_codes} total`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const downloadSlips = async () => {
    try {
      const res = await fetch(`${API}/elections/${el.id}/kiosk/slips.pdf`, { credentials: "include" });
      if (!res.ok) { toast.error("Generate kiosk codes first"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `kiosk_codes_${el.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Slips PDF downloaded");
    } catch { toast.error("Download failed"); }
  };
  const kioskLink = `${window.location.origin}/kiosk/${el.id}`;

  return (
    <div className="bg-white border border-zinc-200 p-6 max-w-2xl" data-testid="kiosk-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold text-xl tracking-tight mb-1 flex items-center gap-2"><IdentificationCard size={20} weight="duotone" /> Kiosk voting</h3>
          <p className="text-sm text-zinc-500">Run an election-day station. Print personal codes, hand them out, and voters type their code at the kiosk to cast — the screen resets after each voter.</p>
        </div>
        <Switch data-testid="kiosk-toggle" checked={enabled} onCheckedChange={toggle} disabled={el.status === "closed"} />
      </div>
      {enabled && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-2">
            <input readOnly value={kioskLink} data-testid="kiosk-link" className="flex-1 text-sm font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-zinc-600" />
            <a data-testid="open-kiosk" href={kioskLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium hover:border-black transition-colors"><ArrowSquareOut size={15} /> Open</a>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button data-testid="kiosk-generate" onClick={() => generate(false)} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F6FE5] text-white rounded-md font-semibold text-sm hover:bg-[#1552C9] transition-colors active:scale-95 disabled:opacity-50">
              <IdentificationCard size={15} weight="fill" /> Generate codes
            </button>
            <button data-testid="kiosk-download" onClick={downloadSlips} className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 rounded-md font-semibold text-sm hover:border-black transition-colors active:scale-95">
              <DownloadSimple size={15} /> Download printable slips
            </button>
            <button data-testid="kiosk-regenerate" onClick={() => { if (window.confirm("Regenerate ALL kiosk codes? Every previously printed slip will stop working.")) generate(true); }} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 rounded-md font-semibold text-sm text-zinc-600 hover:border-red-400 hover:text-red-500 transition-colors active:scale-95 disabled:opacity-50">
              Regenerate
            </button>
            {count != null && <span data-testid="kiosk-count" className="text-xs text-zinc-500">{count} voter(s) have codes</span>}
          </div>
          <p className="text-xs text-zinc-400">Codes are generated for every validated voter without one. Only voters who haven't yet voted can use a code. {!el.published && "Codes work once the election is published and live."}</p>
        </div>
      )}
    </div>
  );
}
